import React from 'react';
import { render, waitFor } from '@testing-library/react';

import useUserEvents, { BULK_IMPORT_COMPLETED } from './useUserEvents';
import {
  fetchUserEvents,
  markUserEventsRead,
} from '../services/bulkUserImportService';

jest.mock('../services/bulkUserImportService', () => ({
  fetchUserEvents: jest.fn(),
  markUserEventsRead: jest.fn(),
}));

const onEvents = jest.fn();

function Probe(props) {
  useUserEvents(onEvents, props);

  return <div />;
}

const events = [{ id: 1, kind: BULK_IMPORT_COMPLETED, payload: {} }];

beforeEach(() => {
  // mockReset, not mockClear: one example replaces the handler with one that
  // throws, and mockClear leaves that implementation in place for every example
  // after it.
  onEvents.mockReset();
  fetchUserEvents.mockReset();
  markUserEventsRead.mockReset();
  fetchUserEvents.mockResolvedValue([]);
  markUserEventsRead.mockResolvedValue();
});

describe('management/Users useUserEvents', () => {
  // An import that finished while the admin was on another page should surface
  // as soon as they arrive, not one interval later.
  it('asks once on mount', async () => {
    render(<Probe />);

    await waitFor(() => expect(fetchUserEvents).toHaveBeenCalled());
    expect(fetchUserEvents).toHaveBeenCalledWith([BULK_IMPORT_COMPLETED]);
  });

  it('hands the events over and consumes them', async () => {
    fetchUserEvents.mockResolvedValue(events);

    render(<Probe />);

    await waitFor(() => expect(onEvents).toHaveBeenCalledWith(events));
    await waitFor(() => expect(markUserEventsRead).toHaveBeenCalledWith([1]));
  });

  it('consumes nothing when there is nothing to report', async () => {
    render(<Probe />);

    await waitFor(() => expect(fetchUserEvents).toHaveBeenCalled());
    expect(onEvents).not.toHaveBeenCalled();
    expect(markUserEventsRead).not.toHaveBeenCalled();
  });

  // Consuming after handing over is what makes a dropped toast recoverable:
  // the event stays unread and the next poll collects it again.
  it('does not consume events it could not hand over', async () => {
    fetchUserEvents.mockResolvedValue(events);
    onEvents.mockImplementation(() => {
      throw new Error('render failed');
    });

    render(<Probe />);

    await waitFor(() => expect(onEvents).toHaveBeenCalled());
    expect(markUserEventsRead).not.toHaveBeenCalled();
  });

  it('survives a failed request', async () => {
    fetchUserEvents.mockRejectedValue(new Error('network'));

    render(<Probe />);

    await waitFor(() => expect(fetchUserEvents).toHaveBeenCalled());
    expect(onEvents).not.toHaveBeenCalled();
  });

  // Without an in-flight guard a poll slower than the interval overlaps the
  // next one, both see the same unread events, and the admin gets the same
  // toast twice.
  it('does not start a second poll while one is still running', async () => {
    let release;
    fetchUserEvents.mockReturnValue(
      new Promise((resolve) => {
        release = () => resolve(events);
      }),
    );

    render(<Probe pollInterval={5} />);

    await waitFor(() => expect(fetchUserEvents).toHaveBeenCalled());
    // Several ticks pass while the first request is still outstanding.
    await new Promise((resolve) => {
      setTimeout(resolve, 60);
    });

    expect(fetchUserEvents).toHaveBeenCalledTimes(1);

    // Point later ticks at an empty answer before letting the outstanding one
    // land: the mock hands back one promise for every call, so once it resolves
    // every subsequent tick would redeliver the same events. The server does
    // not do that — they are marked read — and the point here is the guard, not
    // what an already-consumed event would do.
    fetchUserEvents.mockResolvedValue([]);
    release();

    await waitFor(() => expect(onEvents).toHaveBeenCalledTimes(1));
    expect(markUserEventsRead).toHaveBeenCalledTimes(1);
  });

  it('does not poll when disabled', async () => {
    render(<Probe enabled={false} />);

    await new Promise((resolve) => {
      setTimeout(resolve, 20);
    });

    expect(fetchUserEvents).not.toHaveBeenCalled();
  });
});
